import type { TermProfile } from '@/api/terminologyApi'

/**
 * The last terminology profile list the backend returned, kept in localStorage
 * so a page load does not have to wait for `GET /terms/profiles` before it can
 * render (z8nrz7fxhz).
 *
 * The list is global, not per tenant: the route is `@Public` and the backend's
 * tenant scoping of terminology is still a stub. So one key, like the
 * namespace cache (`cgt_terms_*`) it sits next to.
 *
 * There is no expiry. Every load that boots from this copy also fetches a
 * fresh list in the background and writes it back, so the copy is never older
 * than the previous page load.
 */
export const PROFILES_STORAGE_KEY = 'cgt_terminology_profiles'

/** Bump when the stored shape changes; an old shape then reads as no cache. */
const SHAPE_VERSION = 1

type StoredProfiles = { v: number; profiles: TermProfile[] }

type ReadableStorage = Pick<Storage, 'getItem'>
type WritableStorage = Pick<Storage, 'setItem'>

function isProfile(value: unknown): value is TermProfile {
  if (!value || typeof value !== 'object') return false
  const p = value as Record<string, unknown>
  return (
    typeof p.id === 'string' &&
    (p.fallback === null || typeof p.fallback === 'string')
  )
}

/**
 * The cached list, or `null` when there is none or it cannot be trusted
 * (unreadable storage, bad JSON, an older shape). `null` means "boot the way a
 * first visit does": wait for the server.
 */
export function readCachedProfiles(storage: ReadableStorage | null): TermProfile[] | null {
  if (!storage) return null
  let raw: string | null
  try {
    raw = storage.getItem(PROFILES_STORAGE_KEY)
  } catch {
    return null
  }
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<StoredProfiles>
    if (parsed?.v !== SHAPE_VERSION || !Array.isArray(parsed.profiles)) return null
    if (!parsed.profiles.every(isProfile)) return null
    return parsed.profiles
  } catch {
    return null
  }
}

/** Store the list. A full or blocked storage is not an error: the next load just waits. */
export function writeCachedProfiles(storage: WritableStorage | null, profiles: TermProfile[]): void {
  if (!storage) return
  try {
    const value: StoredProfiles = { v: SHAPE_VERSION, profiles }
    storage.setItem(PROFILES_STORAGE_KEY, JSON.stringify(value))
  } catch {
    // quota exceeded / SecurityError: nothing to do
  }
}

/**
 * Do two lists give a different locale graph? Only `id` and `fallback` feed
 * the fallback chain and the server-locale gate, so a label rename alone does
 * not count: nothing on screen reads labels from this list.
 */
export function localeGraphChanged(before: TermProfile[], after: TermProfile[]): boolean {
  const graph = (list: TermProfile[]) => new Map(list.map((p) => [p.id, p.fallback]))
  const a = graph(before)
  const b = graph(after)
  if (a.size !== b.size) return true
  for (const [id, fallback] of b) {
    if (!a.has(id) || a.get(id) !== fallback) return true
  }
  return false
}
